import React, { ReactElement, useCallback, useMemo, useRef } from 'react';
import { createClientLib } from '@walkme/client-lib';
import config from '../../config/config';
import { PerfLogTypes, ShellSDK } from '@walkme/client-shell-sdk';
import { isProdEnv } from '../../services/environment.service';
import _keyBy from 'lodash/keyBy';
import { CC_PERMISSIONS, IEnvironment, ISystem, PERM_TYPES } from '@communication-center/data-objects';
import _isEqual from 'lodash/isEqual';
import { EnvironmentData, WalkmeSdkContextType, WalkmeSdkService } from './types';
import { WalkmeSdkContext } from './useWalkmeSdkService';
import { getCalculatedRole, loadWalkmeSnippet, parseEnvironment, parseSystem, parseSystemData } from './utils';
import type { Permission, User } from 'wm-accounts-sdk';

let _extraAppData: Parameters<typeof ShellSDK.prototype.sendExtraAppData>[0];

const WalkmeSdkProvider = ({ children }: { children: ReactElement }) => {
    const shellSdk = useMemo(() => {
        return new ShellSDK(config.shellSdkAppName);
    }, []);

    const clientSdkRef = useRef<ReturnType<typeof createClientLib>>(null);
    const getClientSdk = useCallback(async () => {
        if (!clientSdkRef.current) {
            performance.mark('init-client-sdk-start');
            const token = (await shellSdk.getToken()) as { access_token: string };
            const client = createClientLib(token?.access_token, {
                osURL: config.osUrl,
                accountsURL: config.accountsDomainUrl
            });
            performance.mark('init-client-sdk-end');
            performance.measure('measure-init-client-sdk-duration', 'init-client-sdk-start', 'init-client-sdk-end');
            Object.assign(clientSdkRef, { current: client });
        }
        return clientSdkRef.current;
    }, [shellSdk]);

    const getEnvironments = useCallback(async (): Promise<IEnvironment[]> => {
        const walkmeEnvironmentsData = (await shellSdk.getEnvironments()) as EnvironmentData[];
        return walkmeEnvironmentsData.map(env => parseEnvironment(env));
    }, [shellSdk]);

    const getCurrentEnvironment = useCallback(async () => {
        const walkmeEnvironment = (await shellSdk.getEnvironmentData()) as EnvironmentData;

        return parseEnvironment(walkmeEnvironment);
    }, [shellSdk]);

    const getSystems = useCallback(async () => {
        const walkmeSystems = (await shellSdk.getSystems()) as ISystem[];
        const systems = walkmeSystems.map(system => parseSystem(system));
        return _keyBy(systems, 'guid');
    }, [shellSdk]);

    const getSystemData = useCallback(async () => {
        const walkmeSystem = (await shellSdk.getSystemData()) as ISystem;

        return parseSystemData(walkmeSystem);
    }, [shellSdk]);

    const setNavConfirmRequired = useCallback((isRequired: boolean) => shellSdk.setNavConfirmRequired(isRequired), [shellSdk]);

    const sendLoadEndEvent = useCallback(() => {
       shellSdk.logPerfEvent(PerfLogTypes.LOAD_END);
    }, [shellSdk]);

    const onSystemChange: WalkmeSdkService['onSystemChange'] = useCallback(
        async (system: ISystem) => {
            const [{ currentSystem, currentNumericSystemId, currentSystemTypeKey, walkmeFeatureFlags }, environments, currentEnvironment] = await Promise.all([
                parseSystemData(system),
                getEnvironments(),
                getCurrentEnvironment()
            ]);

            return { currentSystem, currentNumericSystemId, currentSystemTypeKey, environments, currentEnvironment, walkmeFeatureFlags };
        },
        [getCurrentEnvironment, getEnvironments]
    );

    const getLoggedInUserData = useCallback(async () => {
        performance.mark('get-user-data-start');
        const userData = await shellSdk.getUserData() as User
        performance.mark('get-user-data-end');

        const { id, email, firstName, lastName, impersonator, role } = userData;
        const label = [firstName, lastName].filter(Boolean).join(' ');

        const calculatedRole = getCalculatedRole(role?.permissions);
        const permissions = role?.permissions.reduce((acc, { permission, actionKey }: Permission) => {
            if (CC_PERMISSIONS[actionKey]) {
                acc[actionKey] = permission as PERM_TYPES;
            }
            return acc;
        }, {} as Record<CC_PERMISSIONS, PERM_TYPES>);

        // for walkme end user id
        window['user'] = email;
        window['role'] = calculatedRole;

        return {
            id,
            label: label || email,
            email,
            impersonator,
            role: {
                id: role?.id,
                displayName: calculatedRole,
                permissions
            }
        };
    }, [shellSdk]);

    const changeRoute = useCallback(
        (route: string) => {
            if (route === '/') {
                route = '';
            }
            return shellSdk.changeRoute(route);
        },
        [shellSdk]
    );

    const sendExtraAppData = useCallback(
        (extraAppData: typeof _extraAppData) => {
            if (!_isEqual(extraAppData, _extraAppData)) {
                _extraAppData = extraAppData;
                shellSdk.sendExtraAppData(extraAppData);
            }
        },
        [shellSdk]
    );

    const _waitForPublishResult = useCallback(
        async ({ tasks, transactionId }: $TSFixMe, frequency = 300, intervals = 40) => {
            return new Promise((resolve, reject) => {
                let pastIntervals = intervals;

                const timer = setInterval(async () => {
                    const { content: progress } = await (await getClientSdk())!.getPublishProgress({ transactionId });
                    const groups = (progress.tasks as Array<$TSFixMe>).reduce((groups: $TSFixMe, task: $TSFixMe) => {
                        groups[task.status] = groups[task.status] || [];
                        groups[task.status].push(task);
                        return groups;
                    }, {});
                    const { pending = [], done = [], failed = [] } = groups;
                    const inProgress = groups['in-progress'] || [];

                    const success = done.length === tasks.length;

                    if (success) {
                        clearInterval(timer);
                        resolve({ success, tasks: { done, pending, ['in-progress']: inProgress, failed } });
                    } else if ((pending.length === 0 && inProgress.length === 0) || failed.length > 0 || pastIntervals-- <= 0) {
                        clearInterval(timer);
                        reject({ success: false, tasks: { done, pending, ['in-progress']: inProgress, failed } });
                    }
                }, frequency);
            });
        },
        [getClientSdk]
    );

    const publishData = useCallback(
        async (contentsIds: Array<{ type: number; checkedIds: Array<string | number> }> = [], environmentId?: $TSFixMe, isImpersonated = false) => {
            const walkmeEnvironment = parseInt(environmentId || (await getCurrentEnvironment()).id, 10);
            const checkedItemsIds = [...contentsIds];

            const { content: workflow } = await (await getClientSdk())!.startPublish({
                isImpersonated,
                params: {
                    skipSettings: true,
                    environmentId: walkmeEnvironment,
                    checkedItemsIds: JSON.stringify(checkedItemsIds)
                }
            });

            // poll for workflow progress at a frequency of 500ms
            const publishResult = await _waitForPublishResult(workflow, 500);

            return { data: publishResult };
        },
        [_waitForPublishResult, getClientSdk, getCurrentEnvironment]
    );

    const authorize: WalkmeSdkService['authorize'] = useCallback(async () => {
        await shellSdk.connect();
        shellSdk.logPerfEvent(PerfLogTypes.LOAD_START);

        performance.mark('authorization-start');

        const [environments, currentEnvironment, systems, systemData, user] = await Promise.all([
            getEnvironments(),
            getCurrentEnvironment(),
            getSystems(),
            getSystemData(),
            getLoggedInUserData()
        ]);

        isProdEnv(config.getENV()) && !window['_walkMe'] && loadWalkmeSnippet();

        const data = {
            user: user!,
            environments,
            currentEnvironment,
            systems,
            ...systemData
        };

        performance.mark('authorization-end');

        return { data };
    }, [getCurrentEnvironment, getEnvironments, getLoggedInUserData, getSystemData, getSystems, shellSdk]);

    const walkmeSdkService = useMemo(
        () => ({
            authorize,
            sendExtraAppData,
            changeRoute,
            sendLoadEndEvent,
            onSystemChange,
            publishData,
            setNavConfirmRequired
        }),
        [authorize, sendExtraAppData, changeRoute, sendLoadEndEvent, onSystemChange, publishData, setNavConfirmRequired]
    );

    const value: WalkmeSdkContextType = useMemo(
        () => ({
            shellSdk,
            walkmeSdkService
        }),
        [shellSdk, walkmeSdkService]
    );

    return <WalkmeSdkContext.Provider value={value}>{children}</WalkmeSdkContext.Provider>;
};

export default WalkmeSdkProvider;
